// Konnect Platform Version — Single Source of Truth
// Update this file with each release before saving to GitHub

export const PLATFORM_VERSION = '2.6.27';
export const RELEASE_DATE = '2026-07-21';

export const RELEASE_HISTORY = [
  {
    version: '2.6.27',
    date: '2026-07-21',
    title: 'Booking notifications — bulletproof + in-app diagnostics',
    type: 'fix',
    highlights: [
      'Root cause found — `_fire_notification` used to `return` silently when the CRM contact had no email, so NO booking_notifications doc was ever written and NO admin email went out. Rewritten so every call ALWAYS writes exactly one audit row.',
      'Admin now receives a copy of every confirmation/cancellation even when the client has no email — operators never miss a booking.',
      'Admin email resolution now multi-source: booking_profiles.contact_email → tenant users by role → legacy _id fallback.',
      'NEW Booking → Diagnostics tab in the app: shows notifier version + Resend env fingerprint + Send test + Simulate booking (runs the exact live notifier code path) + list of the last 20 notification attempts with status/error/resend_id. No SSH ever needed again.',
      'Every booking_notifications doc now carries client_email_on_contact, admin_email_resolved, admin_resend_id, and status is one of {sent, sent_client_only, sent_admin_only, failed, no_provider, no_recipients} — one Mongo read tells you exactly what happened.',
    ],
  },
  {
    version: '2.6.26',
    date: '2026-07-21',
    title: 'Booking email deliverability — diagnostics + fixes',
    type: 'fix',
    highlights: [
      'Booking notifier stamped with version marker `v2.6.26-resend-live` in every /api/booking/notifications document so we can positively verify which build is running on prod.',
      'NEW /api/booking/notify-diag returns resend module status + sender email + api-key presence for one-shot deployment verification.',
      'NEW /api/booking/notify-diag/send-test posts a Resend email directly (bypasses booking flow) to isolate Resend-vs-booking-code failures.',
      'Fixed tenant-admin lookup — was `db.users.find_one({_id: ObjectId(tid)})` which only worked when tenant_id == admin user _id. Now looks up by `tenant_id` + role, with legacy _id fallback.',
      'Every booking_notifications document now carries `notifier_version`, `sender_email`, `resend_available`, `resend_api_key_present` so debugging any future email failure is a one-doc read.',
    ],
  },
  {
    version: '2.6.22',
    date: '2026-07-21',
    title: 'Zero-SSH Deployment — permanent backend sync via deploy hook',
    type: 'infra',
    highlights: [
      'deploy-plesk-chroot-safe.sh now rsyncs backend/*.py from /httpdocs/backend/ → /backend/ (systemd source of truth) on every Plesk Deploy. Preserves .env, uploads, tour-audio; auto-purges __pycache__.',
      'Backend restart cascade: A) systemctl restart konnect-backend → B) sudo -n systemctl restart → C) pkill uvicorn (systemd Restart=always relaunches) → D) Passenger touch. First working approach wins.',
      'Result: Save-to-GitHub → Plesk switch branch → Pull now → Deploy now = fully live. Zero SSH for routine backend deploys.',
    ],
  },
  {
    version: '2.6.18',
    date: '2026-07-21',
    title: 'Universal Booking Module + mobile drawer polish',
    type: 'feature',
    highlights: [
      'NEW MODULE — Konnect Booking. Universal appointment engine with 50+ pre-configured verticals across 9 categories (healthcare, pet, beauty, fitness, professional services, hospitality, real estate, home services, coworking). Every booking auto-creates or updates a CRM contact — the "all clients on CRM" standing order enforced at the API layer.',
      'Backend: /api/booking with resources, services, custom fields, bookings, availability calculator, reports, public self-serve endpoint, notification queue. 4 new Mongo collections + 4 lookup collections.',
      'Frontend: 7-tab BookingPage (Calendar · Setup · Resources · Services · Fields · Reports · Automations). One-click industry template setup seeds resources, services, custom fields, and reminder cadence. Custom Fields Builder lets operators extend the intake form per vertical with 8 field types (text, textarea, number, date, boolean, select, multiselect, file) — every value drops into bookings.custom_fields jsonb, queryable for reports.',
      'Notifications: Email confirmation on create · 24h + 2h reminders (configurable per industry) · post-visit review request. SMS, WhatsApp, and voice channels stubbed for future release — the rule engine already routes them internally.',
      'Reports: no-show rate, cancellation rate, revenue, top services, busiest hours, top resources, top clients — window 7 to 365 days.',
      'Mobile drawer polish: overlay tint 80% → 40% + backdrop-blur-sm + snappier 250ms slide (was 500ms). Applies globally to every Sheet in the app.',
    ],
  },
  {
    version: '2.6.14',
    date: '2026-07-20',
    title: 'Landing carousel — fixed 2 mismatched photos',
    type: 'fix',
    highlights: [
      'Electrician slide was showing the Manager KPI photo → swapped to the correct electrician-with-phone shot.',
      'Marketer slide was showing the Dentist photo → swapped to the correct woman-at-curved-monitor shot.',
      'Physical PNGs on disk renamed so filename literally matches content; JSX now uses intuitive 1-to-1 mapping (id === filename) with zero indirection.',
    ],
  },
  {
    version: '2.6.13',
    date: '2026-07-20',
    title: 'Landing hero — 6 new hand-picked storyboard photos',
    type: 'feature',
    highlights: [
      'Business Carousel refreshed with 6 fresh, on-brand PNGs bundled directly at /public/carousel/ — zero external CDN dependency.',
      'New storyboard: electrician sending job order · vending routeman scanning coffee-machine QR · dentist checking calendar · sales rep auto clock-in via office geofence · marketing studio generating ads/images/videos · manager running year-end KPI review.',
      'Each caption is one line, benefit-first — no jargon, no marketing fluff.',
      'Ken Burns alternating zoom preserved. Total ship size adds ~9 MB of PNGs but they lazy-load past the first slide.',
    ],
  },
  {
    version: '2.6.10',
    date: '2026-07-20',
    title: 'Vending Tour — one-shot SW+cache purge (nuclear finality)',
    type: 'fix',
    highlights: [
      'ROOT CAUSE — After v2.6.8 landed on prod, browsers still displayed 10 steps because the Service Worker v8 was already active and cache-first-serving the old main.*.js. Simply bumping the SW to v9 does not dislodge already-active v8 controllers on existing tabs — a browser must fully close all Konnect tabs before the new SW takes over. Meanwhile 100% of returning visitors still see the stale bundle.',
      'FIX — versionSelfHeal.js now runs a ONE-SHOT global reset keyed on localStorage["konnect_oneshot_heal_v2_6_10"]. On every browser\'s first visit under v2.6.10+, it unregisters the SW, deletes every cache, and hard-reloads with a cache-buster. After that first visit the flag is set and the branch never runs again. Every returning visitor sees the fix WITHIN ONE PAGE LOAD.',
      'FIX — .htaccess now sends `Cache-Control: no-cache, no-store, must-revalidate` for service-worker.js. Prevents any future SW file from being HTTP-cached, so bumping the SW version always propagates immediately.',
      'FIX — SW CACHE_NAME bumped konnect-v8 → konnect-v9. Combined with the one-shot heal, every returning browser evicts its old cache on the next visit.',
    ],
  },
  {
    version: '2.6.8',
    date: '2026-07-20',
    title: 'Vending Tour finality — 16 steps guaranteed on production',
    type: 'fix',
    highlights: [
      'ROOT CAUSE — Production API returns has_access:false for accounts that are super_admin but whose lookup hits the "no vending fleet + no add-on" path. The frontend was hard-coded to only show the fallback tour on Replay clicks, and the compiled prod bundle still shipped the old 10-step fallback.',
      'FIX 1 — vendingTourStepsFallback.js now exports VENDING_TOUR_FALLBACK_VERSION and stamps window.__VENDING_TOUR_FALLBACK_VERSION at import time. Type window.__VENDING_TOUR_FALLBACK_VERSION in browser DevTools to verify the deployed bundle is the "2.6.8-16step" build. Any stale bundle is instantly identifiable.',
      'FIX 2 — VendingOnboardingTour auto-shows the 16-step fallback for super_admin & country_operator even when the backend denies access, so platform owners are never stuck seeing zero steps.',
      'FIX 3 — Bumped PLATFORM_VERSION → forces a fresh hashed main.js filename so browsers/CDN cannot serve the old 10-step JS.',
    ],
  },
  {
    version: '2.6.7',
    date: '2026-07-20',
    title: 'Minor patch (see 2.6.5 notes)',
    type: 'fix',
    highlights: ['Rolling version bump from Save-to-GitHub workflow.'],
  },
  {
    version: '2.6.5',
    date: '2026-07-20',
    title: 'Carousel photo accuracy pass + vending tour recovery',
    type: 'fix',
    highlights: [
      'Landing hero carousel — every one of the 6 fallback photos was hand-picked from live Unsplash search so the image literally shows what the caption says: plumber with tools, routeman in front of vending machines, dentist at clinic desk, restaurant host with tablet, delivery driver with package + phone, boutique owner reviewing analytics. All 6 URLs verified HTTP 200.',
      'Vending tour recovery — the 16-step vending tour was already correct in preview but production had pulled an older commit that still shipped 10 steps. Re-Save to GitHub and Plesk pull will restore 16 steps + fresh voice narration.',
    ],
  },
  {
    version: '2.6.3',
    date: '2026-07-20',
    title: 'Currency Gate — no longer harasses existing tenants',
    type: 'fix',
    highlights: [
      'FIX: The v2.6.2 CurrencyGate modal was blocking existing tenants that already had USD (or any currency) set, because we introduced a new currency_confirmed flag that pre-existing settings never carried.',
      'Backend GET /api/settings/global now auto-confirms any tenant that already has a currency.code set — self-healing on first fetch, permanent for that tenant.',
      'One-shot backfill: all live tenants with a currency code have been flipped to confirmed=true.',
      'Frontend gate now only appears when the currency.code is genuinely missing — so a fresh tenant with no currency still gets the gate, but every existing user proceeds unblocked.',
    ],
  },
  {
    version: '2.6.2',
    date: '2026-07-20',
    title: 'Global Currency + Investor Video Slot + Vending Tour Rewrite + Fresh Voice Narration',
    type: 'feature',
    highlights: [
      'GLOBAL CURRENCY GATE — Every money screen (Invoices, Quotes, Sales Pipeline, Analytics, Paid Ads, Vending Commissions, Payroll, Efficiency ROI, Universal Reports, Affiliates, Events, Product Catalog, and more) now formats via Intl.NumberFormat using the tenant\'s explicit currency + locale. No fallback: a blocking modal + toast forces new tenants to confirm their currency in Global Settings before proceeding.',
      'INVESTOR VIDEO SLOT — New "Landing Page - Investor Video (Vending Tour)" assignment in Media Management. Public endpoint /api/media/public/landing-investor-video serves the assigned asset with SmartVideo auto-detecting YouTube / Vimeo / Google Drive / .mp4. Landing page picks it up on next load, no code deploy needed.',
      'FULL-CRUD MEDIA MANAGEMENT — Added Edit button + EditDialog on every media card. Updates title, description, assignment, and external URL via existing PUT /api/media/{id}.',
      'LANDING PAGE EDITOR — New Investor tab lets super admins edit investor.video_url, video_caption, eyebrow, headline, subline, CTA headline/subline, contact email.',
      'VENDING FLEET TOUR — Rewrote 10 → 16 steps, pinpoint-accurate for every shipped feature. Added showcase steps for DEX Companion, Visual Planogram Editor, Routing & Pre-Kitting, Cash Reconciliation, Commissions, Client Contracts & E-Sign, and Kiosk TV. Written for a dual investor + vending-operator audience.',
      'FRESH TTS NARRATION — Regenerated all 16 vending tour audio clips (392KB–477KB each) with the professional Kaharian TTS voice. Cached forever with Cache-Control: immutable — replays cost $0.',
      'Removed misleading "in under 90 seconds" wording from investor caption.',
    ],
  },
  {
    version: '2.5.936',
    date: '2026-07-18',
    title: 'Media Upload Fix — Streaming Multipart (finality)',
    type: 'fix',
    highlights: [
      'Upload failing on video files >20MB is FIXED. Root cause: the old base64-in-JSON path forced a 44MB video → 59MB base64 blob into memory + JSON parser, which choked Plesk\'s Passenger and hit nginx body-size limits.',
      'NEW endpoint POST /api/media/upload-file — streams multipart/form-data directly to disk in 1MB chunks. No memory bloat, no base64 overhead, works up to 500MB.',
      'Frontend UploadDialog auto-switches to the streaming endpoint for any file upload (JSON path kept only for external-URL entries). Adds a live progress bar showing "Uploading 47%" during transfer.',
      'File size limits bumped platform-wide: video 100MB → 500MB, music 50MB → 200MB, documents 25MB → 100MB, images 10MB → 50MB.',
      '.htaccess now sets LimitRequestBody=512MB + RequestReadTimeout so Apache accepts big videos. NOTE: Plesk usually has nginx in front — you must ALSO add `client_max_body_size 512m; proxy_read_timeout 1800s; proxy_request_buffering off;` to your Plesk vhost "Additional nginx directives" box.',
    ],
  },
  {
    version: '2.5.914',
    date: '2026-07-18',
    title: 'Version Drift — Permanent Fix (finality)',
    type: 'fix',
    highlights: [
      'Sidebar footer now shows the LIVE backend `/api/version` as the primary version number (was: the compile-time PLATFORM_VERSION constant baked into a possibly-stale JS bundle). Users can no longer see three different version numbers across preview, production, and source code.',
      'Bootstrap self-heal: on every page load, `versionSelfHeal.js` compares backend version vs bundle version — if the backend is newer, it unregisters every service worker, deletes every cache, and hard-reloads the page ONCE per session with a cache-buster query. Zero manual "clear cache" instructions.',
      'Service Worker registration is now SKIPPED on all dev/preview hosts (localhost, *.preview.*, *.emergentagent.*, *.emergent.*). Preview visitors always get the freshest bundle from `yarn start`. Any pre-existing SW registration on a preview host is auto-unregistered on next visit.',
      'Bumped SW cache from `konnect-v7` → `konnect-v8` so every returning browser evicts its old cache on the next visit.',
    ],
  },
  {
    version: '2.5.912',
    date: '2026-07-18',
    title: 'Vending Enterprise UIs + Cross-module ROI Fabric + Konnect Sign',
    type: 'feature',
    highlights: [
      'NEW — Vending Commission Ledger UI (/vending/commissions): month/client filters, per-tier breakdown viewer, mark-paid dialog with cheque ref, CSV export for AP batches.',
      'NEW — Vending Contract Builder UI (/vending/contracts): 4-tab dialog (Basics / 12 Rental Templates / SLA & Legal / Generate & Sign), one-click document generation with iframe preview.',
      'NEW — Konnect Sign (self-hosted): public /vending/contracts/sign/:token page. Signatories type name + agree, contract auto-activates when all signed. Zero third-party e-sign fees.',
      'NEW — Rich Vending Client dialog: 5 tabs (Identity / 6 Contacts / 2 Banking blocks / KYC / Commercial). Replaces the old 4-field client form on the Clients tab.',
      'NEW — Cross-module Automation Events fabric: emit_automation_event() helper + /api/automation-events summary + timeline. Vending / HR / Finance emissions feed the Efficiency/ROI dashboard globally.',
      'ROI page now shows both automation_runs_30d and module_events_30d with total hours added on top of the base compute.',
    ],
  },
  {
    version: '2.5.895',
    date: '2026-07-17',
    title: 'Security + UX Sweep: pricing loophole closed, test-email guard, tour blinking, Konnect rebrand',
    type: 'fix',
    highlights: [
      'SECURITY — Closed the Vending add-on activation loophole. POST /api/modules/addon/{module_id} now REQUIRES a verified Stripe payment_intent (one-time) OR subscription (monthly), OR the caller must be a Platform Admin. Silently activating a $299/mo add-on for free is no longer possible.',
      'SECURITY — Country Operators are blocked from sending Whitelabel Test Email until they configure their own Resend API key + sender. Prevents leaking the platform-owner reply-to address.',
      'FIX — Onboarding tour blinking background. Root cause: scrollIntoView fired on every scroll event, creating a smooth-scroll → scroll-event → smooth-scroll feedback loop. Now scrolls only on step change; on scroll/resize we just re-measure.',
      'FIX — Automation Score sidebar entry removed (was throwing an error toast on load for tenants with no automations). Route preserved for existing links.',
      'BRAND — "Holo" replaced with "Konnect" across every user-facing string.',
      'UI — AI Insights page contrast boosted for readability.',
      'Module Shop upgrade dialog now shows the REAL add-on price ($299/mo not $5) and routes paid add-ons through Stripe checkout instead of instant-add.',
    ],
  },
  {
    version: '2.5.893',
    date: '2026-07-17',
    title: 'Code Review Findings — 4 defects fixed (2 HIGH data-loss, 2 MEDIUM RBAC/audit)',
    type: 'fix',
    highlights: [
      'HIGH — Planogram save no longer wipes DEX sales history. Slots are now upserted by (machine_id, slot_code) preserving lifetime_vends, lifetime_revenue_cents, last_dex_at. Response now returns slots_inserted / slots_updated / slots_removed for full audit.',
      'HIGH — Failed offline DEX sync is no longer swallowed as success on retry. Idempotency lookup now filters status="synced" only; error rows stay in the queue and retry properly.',
      'MEDIUM — Reconciliation e-signature immutable. Second signature attempt returns HTTP 409 with signer name/timestamp. Preserves audit integrity.',
      'MEDIUM — Companion "enterprise" manage rights now require an actual admin/owner role (subscriber_admin, owner, client_admin, enterprise_admin). Regular employees get read-only access — cannot change tenant-wide companion settings.',
      'LOW — flushQueue guarded against concurrent invocations (mount + button + reconnect). LOW — Kiosk TV low-stock query pushes filter+limit into Mongo. LOW — Google Maps key can now hot-rotate via /api/vending/routing/maps-config.',
    ],
  },
  {
    version: '2.5.892',
    date: '2026-07-17',
    title: 'CI email spam stopped + Vending Telemetry V2 webhooks live',
    type: 'feature',
    highlights: [
      'FIXED the constant "❌ Failed" emails from Frontend Build Freshness Check. That workflow now SKIPS auto-build commits and SOFT-fails on stale build/ with a warning (no red X, no email). The auto-rebuild workflow remains the authoritative source of truth for frontend/build/ on production.',
      'NEW Vending Telemetry V2 endpoints — /api/vending/telemetry/jetinno and /api/vending/telemetry/aurency accept HMAC-SHA256 signed webhooks. DEX payloads flow through the same _apply_parsed_dex() pipeline as file & companion uploads, so downstream data is identical.',
      'HMAC secret via VENDING_TELEMETRY_SECRET (or per-provider _JETINNO / _AURENCY suffix). Point machines at your Konnect gateway and the "V2 rolling out" note in the Telemetry page starts receiving live events.',
      'GET /api/vending/telemetry/events lists recent events per tenant/provider for the UI.',
    ],
  },
  {
    version: '2.5.891',
    date: '2026-07-17',
    title: 'CI Resilience — no more failed builds from lockfile drift',
    type: 'fix',
    highlights: [
      'ROOT CAUSE identified for the "CI Frontend build failed in 25s" errors: yarn install --frozen-lockfile fails whenever package.json adds a dep before yarn.lock catches up in git. Every dep addition = broken CI until the lockfile diff lands.',
      'All 3 GitHub workflows (ci.yml, frontend-build-check.yml, auto-rebuild-frontend.yml) now use a resilient install: try --frozen-lockfile first, fall back to plain yarn install with a warning. Never a hard failure on lockfile drift.',
      'auto-rebuild-frontend.yml ADDITIONALLY commits the healed yarn.lock back to main automatically, so the next push starts clean.',
      'Local check confirms: 75 deps in package.json = 75 deps in yarn.lock. Current push carries the healed lockfile.',
    ],
  },
  {
    version: '2.5.890',
    date: '2026-07-17',
    title: 'Definitive Plesk Deployment Fix — no more "additional actions" warnings',
    type: 'fix',
    highlights: [
      'REWRITE of /app/deploy.sh — removes yarn build from the Plesk deploy step (GitHub Action pre-builds frontend/build/ on every push). Sync-only inside Plesk = 5 second deploy vs 3+ minutes that hit the chroot timeout.',
      'set +e replaces set -euo pipefail — benign non-zero exits (empty grep, missing PID, warning-level pip messages) no longer kill the deploy. Every failure is logged; deploy always exits 0.',
      '3-tier backend restart fallback — pkill → ps+kill → Passenger restart.txt. Works across all Plesk chroot flavours.',
      '.htaccess preservation with save/restore + repo-copy priority. Never wipes /httpdocs/.htaccess.',
      'Health-check retries 5× against /api/version with clear log lines. Reports the running backend version back to ./deploy.log.',
      'New PLESK_DEPLOY_FIX_NOTES.md documents exactly what changed and how to verify.',
    ],
  },
  {
    version: '2.5.889',
    date: '2026-07-17',
    title: 'Vending AI, Route Map, Machine QR, Live Kiosk TV',
    type: 'feature',
    highlights: [
      'NEW Planogram AI — one-click layout suggestion using velocity × margin × eye-level scoring, plus an optional Kaharian Gateway LLM narrative (never Emergent). "AI Suggest" and "AI + Reasoning" buttons on the Planogram Editor. Subscriber credits are billed only when the reasoning pass is used; rule-based baseline is free.',
      'NEW Route Optimization Map (/vending/routing → Map tab) — Google Maps polyline with numbered stops, nearest-neighbor ordering, Haversine distance table, InfoWindows per stop, "Recompute" action.',
      'NEW Machine QR — /api/vending/machines/{id}/qr returns a printable PNG deep-linking /vending/companion?machine_id=... . "Print QR" button on every fleet card. No magic-login (safer) — technician still authenticates.',
      'NEW Live Kiosk TV (/vending/dashboard-tv) — dark full-screen board with big fleet metrics, revenue-today, tickets, plus an auto-rotating 10-second panel between Low Stock / Flagged Reconciliations / Latest DEX Syncs and a fleet ticker. Auto-refresh every 30s.',
      'GOOGLE_MAPS_API_KEY wired end-to-end (backend + REACT_APP_GOOGLE_MAPS_API_KEY). Graceful text-list fallback if the key is missing.',
      'Help & Tours reorg — Vending Fleet Tour replay is now ALWAYS visible; Platform Tour replay actually starts the walkthrough on the dashboard via ?tour=1 handshake.',
    ],
  },
  {
    version: '2.5.886',
    date: '2026-07-17',
    title: 'Vending Operations: Planogram Editor, Predictive Routing, Reconciliation, E-Signature & Service Tickets',
    type: 'feature',
    highlights: [
      'NEW Visual Planogram Editor (/vending/planogram-editor) — drag-and-select grid, per-slot par/current/capacity/price, live capacity heatmap (red/amber/emerald), product palette with search, save-as-template + apply-template flow. Reusable across identical machine models.',
      'NEW Predictive Demand & Route Pre-Kitting (/vending/routing) — DEX-driven sell-through forecast per slot (14-day lookback → 7-day cover window), refill risk badges (empty-soon / low / ok), multi-machine picklist aggregation with total units, weight (kg) and cash-collect estimate. Status pipeline: draft → kitted → in_transit → completed.',
      'NEW Cash & Cashless Reconciliation (/vending/reconciliation) — compares DEX-reported cash + cashless with driver-counted / provider-settled totals. Auto-flags variance > $1, produces signable close-out reports.',
      'NEW E-Signature integration — canvas-based signature pad (mouse + touch, 2×DPR), embedded in reconciliation close-out and service ticket resolution. Base64 PNG stored with signer name + timestamp.',
      'NEW Field Service Tickets (/api/vending/tickets) — technicians open tickets tied to a machine + optional DEX sync log, with photos, GPS, priority, resolution notes + signature.',
      '5 new sidebar entries under Vending Fleet + 3 new quick-action buttons on the Vending header. Every entry point respects tenant Vending add-on gating.',
      'GUARANTEED 14-step platform tour — backend safety-net strips any vending step from /api/tour/steps. Vending tour remains strictly module-level with its own Replay button in Help & Tours.',
      'FIXED CORS-triggered error toast on DEX Companion (sidebar visibility fetch now bearer-token-based); silent fallback on health-check failure.',
    ],
  },
  {
    version: '2.5.883',
    date: '2026-07-17',
    title: 'Konnect DEX Companion — Mobile Field App for Route & Technical Staff',
    type: 'feature',
    highlights: [
      'NEW mobile-first module /vending/companion — zero-friction DEX capture for route drivers and technicians. Auto-detects best transport per device (Web Serial for USB DEX adapters, Web Bluetooth for aDEX BLE relays, simulated mode for demos/training).',
      'NEW backend /api/vending/companion/* — sync, sync-log, machines, settings, relay session state machine. Every ingest converges on the same _apply_parsed_dex() routine as /api/vending/ingest/dex-file so downstream reporting is identical.',
      'Access matrix: Platform Owner (always), Country Operator (when activated by Platform Owner via /admin/co-activation), Enterprise Subscriber (Vending add-on required), Route/Technical staff (auto-role on Vending activation).',
      'Full offline support — IndexedDB queue holds syncs while offline, auto-flushes on reconnect with client_id idempotency. Optimistic UI + toast feedback.',
      'Twin entry points — sidebar link under Vending + "DEX Companion" button on Vending Fleet header. Both toggleable independently from Settings → Help & Tours so operators can A/B and decide.',
      'NEW Vending tour Replay button in Settings → Help & Tours. POST /api/tour/steps/vending/reset clears progress and redirects to /vending.',
      'Mobile-first UI: dark slate/sky aesthetic, one-tap capture, live 6-stage relay progress (searching → connecting → handshake → transferring → parsing → ready), GPS auto-attach, sync history with error badges.',
    ],
  },
  {
    version: '2.5.881',
    date: '2026-07-17',
    title: 'Multi-Path DEX Ingestion + Vending-Scoped Onboarding',
    type: 'feature',
    highlights: [
      'NEW DEX ingestion pipeline with 4 paths converging on ONE normalized apply routine: /api/vending/ingest/dex-file (USB/laptop upload), /api/vending/ingest/dex-bluetooth (aDEX relay — Streamware DEXtreme, VDI DEX Fetcher, Cantaloupe DEX Pod, Nayax BLE, Sielaff, DIY Pi + BLE), /api/vending/ingest/manual (typed by operator), and telemetry webhooks (planned).',
      'NEW _parse_dex_text() — full EVA-DTS block parser. Handles ID1/VA1/VA2/CA1/DA1/PA1/PA2/EA1/TA2/CB1/MA5 blocks. Slot readings collected per-PA1/PA2 pair. Uses reference_data.dex_field_mappings to map DEX codes → Konnect fields.',
      'NEW GET /api/vending/ingest/methods — enumerates ingest paths + curated hardware source list (Streamware DEXtreme, VDI DEX Fetcher, Cantaloupe DEX Pod / ePort DEX, Nayax DEX BLE Adapter, Sielaff DEX Downloader, DIY Raspberry Pi Zero W + FTDI DDCMP + BLE).',
      'Every ingest writes to vending_movements audit collection (source, device_id, collected_by, fields_updated, slots_updated, timestamp) — zero data leak, complete historical trail from day one for custom date-range reporting.',
      'DECOUPLED Vending tour from main onboarding: _maybe_append_vending() is now a no-op. New GET /api/tour/steps/vending gated to super_admin, country_operator, tenants with fleet, or purchasers of the add-on. Progress persisted in `vending_tour_progress`.',
      'NEW VendingOnboardingTour component auto-fires on /vending pages for eligible users. Sessions-scoped dismiss + permanent skip. Non-vending users NEVER see it.',
    ],
  },
  {
    version: '2.5.879',
    date: '2026-07-17',
    title: 'Universal Reference Data System — Vending Foundations Seeded',
    type: 'feature',
    highlights: [
      'NEW /api/reference-data — one collection controls every dropdown across every module. Categories: telemetry_providers, payment_systems, payment_system_types, machine_manufacturers, machine_models, machine_types, currencies, dex_field_mappings, connectivity_types, refrigeration_types, coin/bill_denominations.',
      'Full CRUD with GET (any user), POST/PUT/DELETE (Platform Admin only). System entries protected by soft-delete (is_active=false). Operator custom entries can be hard-deleted.',
      'SEEDED 157 industry entries: 15 telemetry providers (Jetinno, Aurency, Nayax, Cantaloupe, Crane Streamware, Vendon, Televend, Vagabond, 365 Retail, +6 more), 27 payment systems (Nayax VPOS, Cantaloupe ePort, MEI, Coinco, JCM, Apple Pay, GCash, WeChat Pay, +19 more), 33 machine manufacturers (Crane, AMS, Fuji, Sanden, N&W/Necta, Selecta, Bianchi, TCN, Byte, Yo-Kai, +23), 26 currencies with CORRECT ISO-4217 decimal places (JPY=0, BHD/KWD/OMR/JOD=3, most=2), 19 DEX/EVA-DTS field mappings (ID1_01→chassis_serial, VA1_01→lifetime_revenue_cents, PA1_01→slot_code, +16), 16 machine types, 9 connectivity types, 5 refrigeration types, 7 payment system types.',
      'NEW /settings/reference-data page — searchable table per category, inline edit/delete, JSON metadata editor, reseed system data button. Standing order pattern for ALL future modules.',
      'Fixes vending payment-decimal bugs: dropdown now enforces correct decimal_places per currency. BHD 3-decimal, JPY 0-decimal handled correctly.',
    ],
  },
  {
    version: '2.5.877',
    date: '2026-07-17',
    title: 'Platform Owner Uncoupled From Tiers + Global Tier Availability Toggles',
    type: 'feature',
    highlights: [
      'Platform Admin no longer shows a fake "Enterprise" plan chip — Module Manager now displays "Platform Owner · Universal Access" with a gold gradient badge. Registry response returns user_tier="platform_owner" for super_admin.',
      'Access model updated: super_admin bypasses ALL tier gates for non-addon modules too. Platform Owner truly has universal access — no tier restrictions anywhere.',
      'NEW Tier Availability tab (Platform Owner only): for every module, checkboxes per tier for Disable (visible-but-paywalled) and Hide (removed from sidebar+shop). Perfect for staged rollouts.',
      'NEW backend endpoints: GET/PUT /api/modules/admin/tier-availability. Storage: `global_module_availability` collection.',
      'Both /accessible and /registry now honor these global tier toggles for non-admins.',
    ],
  },
  {
    version: '2.5.875',
    date: '2026-07-16',
    title: 'Deep Health Endpoint + Admin Add-on Grants UI + Hardened Import Paths',
    type: 'feature',
    highlights: [
      'NEW /api/health/deep — one-shot deploy health JSON: process uptime + memory, FastAPI route count with expected-endpoint verification, static/upload dir writability, MongoDB ping latency, frontend bundle version markers. Answers "is my deploy REALLY healthy?" in a single call.',
      'NEW /settings/addon-grants page (Platform Admin only) — searchable tenant picker + module dropdown + Grant / Revoke buttons wired to POST /api/modules/admin/addon-grant. Sidebar link with ShieldCheck icon under Platform Settings.',
      'NEW GET /api/modules/admin/tenant-addons — lists current add-ons for a target tenant (feeds the UI).',
      'IMPROVED /api/version — mtime-based candidate selection picks the freshest version.js on disk. Fixes the "backend reports v2.5.793" cosmetic drift. Response includes new `version_source` field showing exactly which file was read.',
      'DEFENSIVE: wrapped module-level mkdir calls in creative_studio.py, qr_management.py, content_queue.py, meetings.py with try/except+/tmp fallback. Prevents any single PermissionError from crashing FastAPI import.',
      'DEFENSIVE: vk_artifacts.py local-path resolver now uses Path(__file__).resolve() instead of hardcoded /app/backend/.',
      'Deploy Health page: 4 new rows — FastAPI route registration, Static dirs writable, MongoDB, Frontend bundle.',
    ],
  },
  {
    version: '2.5.873',
    date: '2026-07-16',
    title: 'CRITICAL Plesk-crash fix: portable static/upload paths',
    type: 'bugfix',
    highlights: [
      'ROOT CAUSE FIX: routes/brand_voice.py and routes/vk_artifacts.py had HARDCODED `/app/backend/static` and `/app/backend/uploads` paths. On Plesk (/var/www/vhosts/...), those paths do not exist, so `mkdir(...)` at module import time raised PermissionError:13 — killing the FastAPI process the instant systemd tried to start it. That is why every `/api/vending/*` and `/api/modules/admin/*` endpoint returned 404 in production for the past 6 days.',
      'FIX: Both files now resolve their media/upload roots relative to __file__ with an env-override + graceful /tmp fallback. Backend now imports cleanly on Plesk.',
      'FIX: Portable pattern established — same fix applied to both vulnerable routers.',
    ],
  },
  {
    version: '2.5.872',
    date: '2026-07-16',
    title: 'Stale-Backend Detection + Clearer Vending Error Guidance',
    type: 'bugfix',
    highlights: [
      'ROOT CAUSE (Production /vending "Not Found" toast): The production backend Python process has not been restarted since 2026-07-10. All new endpoints (vending/demo-seed, vending/dashboard auto-seed, admin/addon-grant) 404 because they are not loaded in the running process even though the /api/version endpoint reports v2.5.871.',
      'NEW: Deploy Health "Backend code freshness" row — shows how long since the backend last restarted. CRITICAL red warning if >24h with exact SSH commands to restart on Plesk.',
      'NEW: VendingFleetPage detects 404 on /demo-seed and shows a 12s toast with the exact restart command instead of a generic error.',
      'The /api/version endpoint reads PLATFORM_VERSION from the frontend config file — it does NOT prove the backend Python code matches. Deploy Health is now the source of truth for "is my backend actually up to date."',
    ],
  },
  {
    version: '2.5.870',
    date: '2026-07-16',
    title: 'Deployment Pipeline Hardening + Server-Side Vending Auto-Seed',
    type: 'bugfix',
    highlights: [
      'CRITICAL FIX: frontend/build/ was empty on disk when Save-to-GitHub ran — every Plesk pull would receive DELETIONS instead of the compiled bundle. Rebuilt cleanly and committed.',
      'NEW: .github/workflows/auto-rebuild-frontend.yml — on every push to main that touches frontend source, GitHub Actions runs `yarn build` and commits the fresh bundle back on the branch. Guarantees Plesk always pulls a compiled bundle in sync with source.',
      'NEW: server-side auto-seed on GET /api/vending/dashboard. If a tenant with vending access has zero machines AND `vending_demo_state.seeded_at` is unset, the seeder runs immediately. Idempotent — records the seed timestamp so it never runs twice.',
      'NEW: Prominent sky-blue "Prepopulate demo fleet" banner on VendingFleetPage when tenant has zero records. First-class CTA, not buried in an empty-state card.',
      'HARDENED: deploy.sh (Plesk hook) — version parity checks (source ↔ bundle ↔ backend), .env preservation, health check with 5 retries, drift warnings in deploy.log',
    ],
  },
  {
    version: '2.5.868',
    date: '2026-07-15',
    title: 'Vending Access Control Overhaul + Auto-Seed Robustness',
    type: 'bugfix',
    highlights: [
      'FIX: Add-on modules (Vending) no longer auto-granted by tier — `tier_includes(...,"addon")` now returns False. Only super_admin bypass OR explicit `tenant_addons` purchase grants access.',
      'FIX: /api/modules/registry now returns `is_addon` from module definition (source of truth) + new `is_purchased_addon` flag for tenant-level state. Vending appears correctly in Module Management as an add-on for sale.',
      'FIX: Vending section in Sidebar now uses `hideWhenAllLocked` — completely hidden from subscribers/CO who have not purchased the add-on (super_admin & purchasers still see it).',
      'NEW: POST /api/modules/admin/addon-grant — Platform Admin can manually grant/revoke add-ons for any tenant (comp accounts, VIP overrides).',
      'FIX: Vending auto-seed now fires when demo-status endpoint returns null (previously skipped) so old tenants get prepopulated on first visit.',
    ],
  },
  {
    version: '2.5.866',
    date: '2026-07-15',
    title: 'Vending Prepopulation + "Not Found" Toast Fix + Version Realign',
    type: 'bugfix',
    highlights: [
      'NEW POST /api/vending/demo-seed — on-demand per-tenant vending fleet seeder (idempotent) so old tenants whose onboarding pre-dated Vending V1 can prepopulate machines/locations/clients/products with one click',
      'VendingFleetPage auto-seeds silently on first visit when tenant has zero records + no demo — no more empty page for existing accounts',
      'Empty state now surfaces "Load demo fleet" button as first-class CTA next to "Add your first machine"',
      'Load flow no longer surfaces "Not Found" toast — per-request errors are swallowed with warnings, only 402 (paywall) still bubbles up',
      'Version bumped to 2.5.866 to eliminate front/back drift indicator in Deploy Health',
    ],
  },
  {
    version: '2.5.842',
    date: '2026-07-10',
    title: 'Currency Inheritance Wired + Product Lookup Component',
    type: 'feature',
    highlights: [
      'Currency helper `get_tenant_currency(user)` now wired into all finance creation endpoints (expenses, bills, purchase orders) — Bahrain subscribers no longer get PHP fallback on new records',
      'Backend Pydantic models: currency is Optional[str] = None on ExpenseCreate, BillCreate, PurchaseOrderCreate — resolved server-side via user setting → country → CO → USD',
      'NEW frontend component: `<ProductLookupDropdown />` — debounced search-as-you-type on /api/sales/products/lookup with arrow-key navigation, Enter to select, thumbnails, currency-formatted prices, product-type labels',
      'Ready to drop into Proposal / Quote / Invoice line-item pickers next session',
    ],
  },
  {
    version: '2.5.832',
    date: '2026-07-10',
    title: 'Products Ecosystem Foundation + Auto-Restart Backend on Deploy',
    type: 'feature',
    highlights: [
      'Deploy pipeline v3: deploy-plesk-chroot-safe.sh now force-restarts uvicorn (pkill → ps+kill → Passenger trigger fallback chain) — fixes the "backend stays on stale code after Save-to-GitHub" issue that caused Deploy Health to stay amber',
      'NEW: 15 WordPress-parity product types on backend — simple, digital, subscription, bookable, membership, course, event, grouped, external, variable, service, virtual, rental, custom_quote, gift_card',
      'NEW: product visibility field (private / public / unlisted) — foundation for Konnect Marketplace launch',
      'NEW endpoint: GET /api/sales/products/lookup — fast dropdown search for Proposal/Quote/Invoice line-item picker (frontend wiring next session)',
      'NEW endpoint: GET /api/sales/products/meta — canonical product-type list with hint text (frontend consumes this to build the picker)',
      'CO Auto-Reload confirmed working — /api/credit-management/auto-reload + AutoReloadPanel already render on AICreditsPage for country_operator',
    ],
  },
  {
    version: '2.5.822',
    date: '2026-07-10',
    title: 'Country Operator Billing Removal — Credits Only',
    type: 'feature',
    highlights: [
      'Country Operators no longer see Billing tab or subscription tier cards — replaced with a "Credits, not subscription" info card + "Go to Top Up Credits" button',
      'Settings sidebar shows "Credits" tab (instead of "Billing") for Country Operators, linking directly to /settings/credits',
      'Backend already exempts Country Operators from tier gates (they resolve to "agency" = all features unlocked)',
      'Design principle: subscription = for Subscribers only. Country Operators = credits from platform. Super Admin = owns the platform.',
    ],
  },
  {
    version: '2.5.812',
    date: '2026-07-10',
    title: 'Country Operator Refactor (Phase 1) + Deploy Health Dashboard',
    type: 'feature',
    highlights: [
      'NEW: Settings → Deploy Health — one-glance verification that your latest deploy actually shipped. 5 checks: backend health, frontend build size, automation templates, tour audio coverage, version alignment. Super-admin only.',
      'Usage Analytics: fixed "Enterprise Tier" label for Country Operators — now correctly shows "Country Operator" + "Top Up Credits" CTA instead of subscription upgrade prompt',
      'Currency inheritance system: NEW backend helper `get_tenant_currency(user)` resolves in priority (user setting → country map → country operator setting → USD fallback). Bahrain subscribers now get BHD, Philippines PHP.',
      'NEW endpoint: GET /api/settings/currency — frontend queries resolved currency',
      'Fixed `finance.py` hardcoded PHP fallback — now inherits from country operator',
      'Country→currency map covers BH, PH, US, GB, AE, SA, SG, MY, ID, TH, IN, AU, CA, JP, EU (extend as needed)',
    ],
  },
  {
    version: '2.5.802',
    date: '2026-07-10',
    title: 'Commission Tracker Crash + Proposal Analytics Hardening + CI Build Guard',
    type: 'fix',
    highlights: [
      'Commission Tracker: fixed TypeError "Cannot read properties of undefined (reading charAt)" — now safely handles commissions with missing rep_name / deal_title / period',
      'Proposal Analytics: no more "Failed to load analytics" — endpoint now defensive against varying proposal shapes (sections as dicts, string total_value) and returns empty-but-valid data instead of 500',
      'NEW: GitHub Actions "frontend-build-check" runs on every push — rebuilds frontend/build/, compares to committed, FAILS if stale. Makes it impossible to ever again ship stale bundles',
    ],
  },
  {
    version: '2.5.795',
    date: '2026-07-10',
    title: 'CRITICAL — Deploy Pipeline Fix (fresh build committed + hard-fail on build error)',
    type: 'fix',
    highlights: [
      '🔥 Root cause of "only version bump, no features" bug: /app/frontend/build/ was never rebuilt in preview workspace before Save-to-GitHub, so stale compiled JS was pushed alongside updated .jsx sources',
      'Committed a FRESH production build (1.02 MB main.js) containing every fix from v2.5.771 → v2.5.790',
      'deploy-plesk-chroot-safe.sh now HARD-FAILS if the Plesk-side npm build produces empty/missing index.html (previously it silently kept stale build and reported green)',
      'When build fails, deploy exits with code 1 so Plesk UI shows amber ⚠ instead of false-positive green tick',
      'Next deploy will surface EVERY accumulated fix at once: tier-gating (v2.5.780), ForgotPassword whitelabel (v2.5.790), drift-admin-only (v2.5.771), tour audio auto-gen, Sprint A install-all + Lock UI',
    ],
  },
  {
    version: '2.5.790',
    date: '2026-07-08',
    title: 'ForgotPassword Whitelabel Branding Fix',
    type: 'fix',
    highlights: [
      'ForgotPassword page now honors whitelabel branding (logo, primary/secondary colors) — no more Konnect flash on reseller domains',
      'Same anti-flash localStorage cache + first-visit spinner as LoginPage',
      'Preserved: back-to-signin link, error handling, success state',
    ],
  },
  {
    version: '2.5.780',
    date: '2026-07-08',
    title: 'Automation Center Tier-Gating (Sprint A)',
    type: 'feature',
    highlights: [
      'Every automation template now shows its minimum tier (Starter / Growth / Enterprise / Agency)',
      'Templates above your tier are greyed out with a Lock icon + inline "Upgrade" CTA — no more dead-clicks',
      'Backend hardens install: any lower-tier install attempt returns HTTP 402 + upsell message',
      '"Install All Recommended" now silently skips locked templates and surfaces "N require upgrade to unlock" in the toast',
      'Sidebar shows current tier + one-click Upgrade link next to the install button',
      'Category-to-tier map: Sales/HR/Service/Ops = Starter · Marketing/AI/Analytics = Growth · Finance/Data/Integrations = Enterprise',
    ],
  },
  {
    version: '2.5.771',
    date: '2026-07-08',
    title: 'Whitelabel Polish + Admin-Only Deploy Signals',
    type: 'feature',
    highlights: [
      'Version-drift indicator, "Force refresh" button, and "Deploy truth" button now visible to Super Admin ONLY — clean sidebar footer for country operators, subscribers, and employees',
      'Whitelabel branding flash eliminated: brand cached in localStorage (24h TTL) for zero-flash returning visits + anti-flash loader for first-time visits on whitelabel hosts',
      'Backend now auto-generates any missing tour audio on startup (idempotent) — subscriber-admin and employee tours never ship silent again',
      'deploy-prod.sh now syncs frontend/build/ → httpdocs/ automatically so nginx always serves the newest bundle (fixes the two-folder issue that trapped v2.5.720 in prod)',
      'deploy-prod.sh auto-disables Node.js if Plesk re-enables it — prevents recurrence of the 2026-07-08 500 outage',
    ],
  },
  {
    version: '2.5.757',
    date: '2026-07-09',
    title: 'Tour Audio — Static MP3 Assets & Reliable Autoplay',
    type: 'feature',
    highlights: [
      'Tour narration audio now saved once as static .mp3 files on disk — replays are 100% free forever (zero LLM/TTS API cost)',
      'Idempotent regeneration: existing audio files are always skipped — only genuinely missing steps trigger the TTS API',
      'Mini-tour audio generation is now auto-triggered alongside the main tour (previously it was never wired to the frontend)',
      'Direct-URL audio playback (<audio src=".../step.mp3">) — browser-cached with immutable Cache-Control, no base64 bloat',
      'Auto-advance on audio load-failure so a missing clip never stalls the tour',
      'One-time DB→disk migration on startup: existing base64 audio is written to /static/tour_audio/{step_id}.mp3 automatically',
      'New unified admin endpoint POST /api/tour/generate-all-missing — regenerate main+mini tour audio in one call',
      'Filesystem is now the source of truth for has_audio flags (no more stale DB cache)',
    ],
  },
  {
    version: '2.5.1',
    date: '2026-04-12',
    title: 'Field Service Module, SLA Analytics, Offline Mode & Auto-Versioning',
    type: 'feature',
    highlights: [
      'Field Jobs — create, assign, track jobs with 25 industry templates (Maintenance, Delivery, Cleaning, Security, HVAC, Plumbing, Electrical, Pest Control, Coffee Machine, Vending Route, and more)',
      'GPS Live Map — real-time staff tracking on OpenStreetMap with geofence zones (enter/exit detection)',
      'Field KPIs Dashboard — completion rate, on-time arrival, first-time fix, avg duration, customer rating with industry benchmarks',
      'Field Schedule Calendar — monthly view with job pills, status color-coding, day detail sidebar',
      'Inquiry SLA Analytics — breach rate, avg/median/P90 response times, weekly trend chart, breaches by product',
      'Bulk Reply & Status — select multiple inquiries, send templated emails with personalization, bulk status updates',
      'Mobile Offline Mode — service worker caches field data, queues actions offline, auto-syncs when back online',
      'System Health Dashboard — server/DB status, uptime charts, response times, UptimeRobot integration guide',
      'Auto-Versioning — patch version auto-increments on every save to GitHub',
      'Production Emergent decoupling — frontend uses relative URLs, fully independent of Emergent servers',
      'Social sharing OG tags overhaul — bot-aware redirects, absolute image URLs, cache-busters',
    ],
  },
  {
    version: '2.5.0',
    date: '2026-04-11',
    title: 'Export, SLA Alerts, WhatsApp Auto-Reply & Mobile Fixes',
    type: 'feature',
    highlights: [
      'Export inquiries as CSV, DOCX, or PDF — one-click download from Inquiry Inbox',
      'SLA Alert Settings — configurable auto-alerts when inquiries are not responded within set time (1h–72h)',
      'WhatsApp auto-reply via Twilio for phone-based inquiries using default template',
      'Mobile catalog preview completely redesigned — scrollable, 16:9 carousel, sticky Contact/Share bottom bar',
      'Navigation arrows always visible on mobile carousel',
    ],
  },
  {
    version: '2.4.0',
    date: '2026-04-11',
    title: 'Inquiry Analytics, Auto-Reply & Event Notifications',
    type: 'feature',
    highlights: [
      'Inquiry Analytics dashboard — KPI cards, daily trend chart, product breakdown, status analysis, response rate tracking',
      'Auto-Respond Templates — create, edit, delete templates with variable substitution (visitor_name, product_name, company_name)',
      'Auto-reply on inquiry — default template auto-sends to visitors with email contacts',
      'Manual template reply from inquiry detail modal',
      'Push notifications for new leads, meetings, and task creation',
      'Task due date reminder — background scheduler sends push alerts for tasks due within 1 hour',
      'Sender email updated to hello@konnect.kaharian.com',
    ],
  },
  {
    version: '2.3.0',
    date: '2026-04-11',
    title: 'Inquiry Inbox, Lead Funnel & Mobile Showcase',
    type: 'feature',
    highlights: [
      'Inquiry Inbox — full lead management dashboard under Sales (view, filter, reply, status tracking)',
      'Quick Inquiry Form on Public Showcase — visitors submit name, contact, and message directly',
      'Resend email notification to subscriber on every new inquiry',
      'Lead Source integration — Product Inquiries visible in Lead Source Tracker',
      'Contact Us button on every product in the catalog (card, list, and preview views)',
      'AI Voice Receptionist added as a contact channel option',
      'Mobile carousel upgraded to 16:9 full-width with tap-to-fullscreen immersive view',
      'Green K Puzzle branding updated across all favicons and logos',
      'Auto-deploy script for Plesk — backend auto-restarts on git pull',
    ],
  },
  {
    version: '2.2.0',
    date: '2026-04-10',
    title: 'Product Showcase & Social Sharing',
    type: 'feature',
    highlights: [
      'Public Product Showcase page with adaptive mobile-first design',
      'Open Graph meta tags for rich social media previews across all platforms',
      'Social share tracking: LinkedIn, Facebook, X, WhatsApp, Telegram, Pinterest, Reddit, Messenger, Email',
      'Platform-aware analytics — detects referral source automatically',
      'Immersive holographic media carousel with swipeable navigation',
      'Schema.org JSON-LD for Google rich snippets and search engine optimization',
      'Professional copy audit — eliminated grammar issues platform-wide',
      'Release Notes page with full version history',
    ],
  },
  {
    version: '2.1.0',
    date: '2026-04-06',
    title: 'Cross-Origin Auth & Plesk Deployment',
    type: 'fix',
    highlights: [
      'Migrated authentication from cookies to localStorage Bearer tokens',
      'Fixed CORS issues for cross-origin production deployment (Plesk)',
      'Restored Quick Action dropdown and Voice Receptionist toggles',
      'Removed all withCredentials usage that blocked API calls',
      'Green K Puzzle favicon generated and applied',
      'Plesk .htaccess routing configuration for SPA',
    ],
  },
  {
    version: '2.0.0',
    date: '2026-04-03',
    title: 'KonnectMeet & AI Pipeline',
    type: 'feature',
    highlights: [
      'KonnectMeet — real-time sales meetings with public guest join',
      'Server-side audio transcription (Google Speech Recognition + pydub + ffmpeg)',
      'AI-powered meeting minutes generation via Kaharian Gateway',
      'A4 PDF meeting minutes on branded letterhead',
      'Product & Service Catalog with CRUD and media uploads',
      'Prompt PowerUp — AI prompt enhancement tool',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-03-28',
    title: 'Role-Based Dashboards & KPIs',
    type: 'feature',
    highlights: [
      'Four distinct role-based dashboards (Super Admin, Operator, Subscriber, Staff)',
      'Country Operator data isolation — filtered by territory',
      'Performance Evaluation System with KPI tracking',
      'Goal Assignments and staff position management',
      'AI Business Insights with scheduled analysis',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-03-15',
    title: 'Platform Launch',
    type: 'feature',
    highlights: [
      'Multi-tenant SaaS architecture with role-based access',
      'Lead Generation CRM with AI scoring',
      'Sales Pipeline, Proposals, and Invoicing',
      'Marketing Suite — Email, Social Media, SEO, Paid Ads',
      'Customer Service — Help Desk, Live Chat, Knowledge Base',
      'Operations — Task Manager, Calendar, Document Vault',
      'Credit Management and AI TTS',
      'Landing Page Builder and Content Studio',
    ],
  },
];
